/**
 * Session form (groups).
 */
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Users, AlertCircle, Zap } from 'lucide-react';
import { UsersListColumn } from '@/components/common/UsersListColumn';
import { UnifiedDropZone } from '@/components/common/UnifiedDropZone';
import { Button } from '@/components/forms/Button';
import { Spinner } from '@/components/ui/spinner';
import { SlidePanel } from '@/components/common/SlidePanel';
import { CreateStudentView } from '@/components/students/inperson/CreateStudentView';
import { TutoringSessionOrganizerPickerModal } from './TutoringSessionOrganizerPickerModal';
import { prependUserToUsersSearchCaches } from '@/utils/created-user-profile-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import type { UserWithProfile } from '@/types/user.types';
import {
  getFloatingVscTutors,
  getOrphanStudents,
  getStudentsMatchedToTutor,
  toSpecificMatches,
} from '@/components/sessions/inperson/session-form-utils';
import type { SlotAvailabilityById } from '@/types/slot-availability.types';
import { NEW_SESSION_DROP_ID } from '@/components/sessions/session-configs';
import { toast } from '@/components/ui/toast';
import type { ParticipantAttendanceById } from '@/types/attendance.types';

/** Props for {@link TutoringSessionGroupsSection}. */
interface TutoringSessionGroupsSectionProps {
  selectedTutors: UserWithProfile[];
  selectedStudents: UserWithProfile[];
  selectedTutorIds: string[];
  selectedStudentIds: string[];
  /** Map studentId → tutorId (source of truth for pairs). */
  studentToTutor: Record<string, string>;
  maxTutors: number | null;
  maxStudents: number | null;
  onRemoveTutor: (userId: string) => void;
  onRemoveStudent: (userId: string) => void;
  onUnmatchStudent: (studentId: string, tutorId: string) => void;
  onTutorDoubleClick: (user: UserWithProfile) => void | Promise<void>;
  onStudentDoubleClick: (user: UserWithProfile) => void;
  onAutoAssign: () => void;
  onRequestCreateTutor?: () => void;
  /** True while the auto-assignment proposal API call is in flight. */
  autoAssignPending?: boolean;
  establishmentId?: string;
  establishmentName?: string;
  tutorAvailabilityById?: SlotAvailabilityById;
  tutorAvailabilityHint?: string | null;
  tutorAvailabilityChecking?: boolean;
  studentAvailabilityById?: SlotAvailabilityById;
  studentAvailabilityHint?: string | null;
  studentAvailabilityChecking?: boolean;
  tutorDragDisabled?: boolean;
  tutorDragDisabledTooltip?: string;
  studentDragDisabled?: boolean;
  studentDragDisabledTooltip?: string;
  /** Day of week of the session (0=Sunday..6=Saturday) for presence-day filtering */
  sessionAvailabilityDay?: number;
  /** Attendance by participant (same tag as session detail). */
  attendanceByUserId?: ParticipantAttendanceById;
}

/** Groups section for homework_help, workshop, and group_tutoring (matching drop zone). */
export const TutoringSessionGroupsSection: React.FC<TutoringSessionGroupsSectionProps> = ({
  selectedTutors,
  selectedStudents,
  selectedTutorIds,
  selectedStudentIds,
  studentToTutor,
  maxTutors,
  maxStudents,
  onRemoveTutor,
  onRemoveStudent,
  onUnmatchStudent,
  onTutorDoubleClick,
  onStudentDoubleClick,
  onAutoAssign,
  onRequestCreateTutor,
  autoAssignPending = false,
  establishmentId,
  establishmentName,
  tutorAvailabilityById,
  tutorAvailabilityHint,
  tutorAvailabilityChecking = false,
  studentAvailabilityById,
  studentAvailabilityHint,
  studentAvailabilityChecking = false,
  tutorDragDisabled = false,
  tutorDragDisabledTooltip,
  studentDragDisabled = false,
  studentDragDisabledTooltip,
  sessionAvailabilityDay,
  attendanceByUserId,
}) => {
  const queryClient = useQueryClient();
  const [isFacilitatorCreationOpen, setIsFacilitatorCreationOpen] = React.useState(false);
  const [isStudentPanelOpen, setIsStudentPanelOpen] = React.useState(false);

  const tutorBulkToastRef = React.useRef<{ dismiss: () => void } | null>(null);
  const studentBulkToastRef = React.useRef<{ dismiss: () => void } | null>(null);

  React.useEffect(() => {
    if (tutorAvailabilityChecking) {
      tutorBulkToastRef.current?.dismiss();
      tutorBulkToastRef.current = toast.loading(getErrorMessage('tutorAvailabilityChecking'));
    } else {
      tutorBulkToastRef.current?.dismiss();
      tutorBulkToastRef.current = null;
      if (tutorAvailabilityHint) {
        toast.warning(getErrorMessage(tutorAvailabilityHint), { duration: 6000 });
      }
    }
    return () => {
      tutorBulkToastRef.current?.dismiss();
    };
  }, [tutorAvailabilityChecking, tutorAvailabilityHint]);

  React.useEffect(() => {
    if (studentAvailabilityChecking) {
      studentBulkToastRef.current?.dismiss();
      studentBulkToastRef.current = toast.loading(getErrorMessage('studentAvailabilityChecking'));
    } else {
      studentBulkToastRef.current?.dismiss();
      studentBulkToastRef.current = null;
      if (studentAvailabilityHint) {
        toast.warning(getErrorMessage(studentAvailabilityHint), { duration: 6000 });
      }
    }
    return () => {
      studentBulkToastRef.current?.dismiss();
    };
  }, [studentAvailabilityChecking, studentAvailabilityHint]);

  /** Seed the users-search cache so the new organizer appears immediately. */
  const handleOrganizerCreatedSuccess = React.useCallback(
    (created: UserWithProfile) => {
      prependUserToUsersSearchCaches(queryClient, 'tutor', created);
    },
    [queryClient],
  );

  const handleStudentCreatedSuccess = React.useCallback(
    (created: UserWithProfile) => {
      setIsStudentPanelOpen(false);
      prependUserToUsersSearchCaches(queryClient, 'student', created);
    },
    [queryClient],
  );

  const specificMatches = React.useMemo(() => toSpecificMatches(studentToTutor), [studentToTutor]);

  const getTutorMatchedStudents = React.useCallback(
    (tutorId: string) => getStudentsMatchedToTutor(selectedStudents, studentToTutor, tutorId),
    [selectedStudents, studentToTutor],
  );

  const orphanStudents = React.useMemo(
    () => getOrphanStudents(selectedStudents, studentToTutor),
    [selectedStudents, studentToTutor],
  );

  const floatingVscTutors = React.useMemo(
    () => getFloatingVscTutors(selectedTutors, studentToTutor),
    [selectedTutors, studentToTutor],
  );

  if (!establishmentId) {
    return (
      <div className="mt-4 rounded-lg border border-dashed border-[var(--color-1-nuance-3)] p-4 text-center text-sm text-muted-foreground">
        Sélectionnez un établissement pour afficher les tuteurs et élèves disponibles.
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 min-h-0 grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-3 min-h-0">
          <UsersListColumn
            role="tutor"
            emptyMessage="Aucun tuteur disponible"
            excludedIds={selectedTutorIds}
            pageSize={5}
            onDoubleClick={onTutorDoubleClick}
            onCreateClick={() => setIsFacilitatorCreationOpen(true)}
            initialEstablishmentId={establishmentId}
            initialEstablishmentName={establishmentName}
            dragDisabled={tutorDragDisabled}
            dragDisabledTooltip={tutorDragDisabledTooltip}
            sessionAvailabilityDay={sessionAvailabilityDay}
          />
        </div>

        <div className="col-span-12 lg:col-span-6 min-h-0">
          <div className="section-card relative h-full flex flex-col overflow-hidden">
            <div className="section-header flex-shrink-0 flex items-center justify-between gap-2">
              <h3 className="section-title text-lg flex items-center gap-2">
                <Users className="w-5 h-5" />
                Groupes
                {specificMatches.length > 0 && (
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full border"
                    style={{
                      background: 'color-mix(in srgb, var(--color-3-nuance-1) 15%, transparent)',
                      color: 'var(--color-3-nuance-1)',
                      borderColor: 'var(--color-3-nuance-1)',
                    }}
                  >
                    {specificMatches.length} {specificMatches.length > 1 ? 'matchings' : 'matching'}
                  </span>
                )}
              </h3>
              <Button
                variant="outline"
                size="sm"
                icon={Zap}
                iconPosition="left"
                onClick={onAutoAssign}
                loading={autoAssignPending}
                disabled={
                  selectedTutors.length === 0 || selectedStudents.length === 0 || autoAssignPending
                }
                title="Répartir équitablement les élèves entre les tuteurs"
              >
                Affectation auto
              </Button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto pr-2">
              <UnifiedDropZone
                mode="matching"
                sessionId={NEW_SESSION_DROP_ID}
                tutors={selectedTutors}
                students={selectedStudents}
                maxTutors={maxTutors}
                maxStudents={maxStudents}
                specificMatches={specificMatches}
                getTutorMatchedStudents={getTutorMatchedStudents}
                onRemoveTutor={onRemoveTutor}
                onRemoveStudent={onRemoveStudent}
                onUnmatchStudent={onUnmatchStudent}
                hideTutorMatchCount
                tutorAvailabilityById={tutorAvailabilityById}
                studentAvailabilityById={studentAvailabilityById}
                attendanceByUserId={attendanceByUserId}
              />

              {floatingVscTutors.length > 0 && specificMatches.length > 0 && (
                <div className="mt-3 p-3 rounded-lg border border-[var(--color-1-nuance-3)] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_8%,transparent)]">
                  <p className="text-sm text-[var(--color-1-nuance-1)]">
                    <span className="font-semibold">VSC sans élève :</span>{' '}
                    {floatingVscTutors.map((t) => `${t.firstName} ${t.lastName}`.trim()).join(', ')}{' '}
                    {floatingVscTutors.length === 1
                      ? '— sera ajouté à la séance en tant qu’observateur (sans groupe dédié).'
                      : '— seront ajoutés à la séance en tant qu’observateurs (sans groupe dédié).'}
                  </p>
                </div>
              )}

              {orphanStudents.length > 0 && (
                <div className="mt-3 p-3 rounded-lg border border-[rgb(var(--color-warning)_/_0.4)] bg-[rgb(var(--color-warning)_/_0.08)]">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-[rgb(var(--color-warning))]" />
                    <div className="text-sm text-[rgb(var(--color-warning))]">
                      <p className="font-semibold mb-1">
                        {orphanStudents.length} élève{orphanStudents.length > 1 ? 's' : ''} sans
                        groupe
                      </p>
                      <p className="text-xs">
                        Glissez chaque élève sous un tuteur ou utilisez l'affectation automatique.{' '}
                        {getErrorMessage('orphanStudentsBlockSubmit')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {autoAssignPending && (
              <div
                className="absolute inset-0 z-10 flex flex-col items-center justify-start gap-2 bg-background/80 backdrop-blur-[2px] pt-40 sm:pt-48"
                aria-busy="true"
                aria-live="polite"
              >
                <Spinner size="lg" label="Calcul de la suggestion d'affectation…" />
              </div>
            )}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-3 min-h-0">
          <UsersListColumn
            role="student"
            emptyMessage="Aucun élève disponible"
            excludedIds={selectedStudentIds}
            pageSize={5}
            onDoubleClick={onStudentDoubleClick}
            onCreateClick={() => setIsStudentPanelOpen(true)}
            initialEstablishmentId={establishmentId}
            initialEstablishmentName={establishmentName}
            dragDisabled={studentDragDisabled}
            dragDisabledTooltip={studentDragDisabledTooltip}
            sessionAvailabilityDay={sessionAvailabilityDay}
          />
        </div>
      </div>

      <TutoringSessionOrganizerPickerModal
        open={isFacilitatorCreationOpen}
        onOpenChange={setIsFacilitatorCreationOpen}
        onOrganizerCreated={handleOrganizerCreatedSuccess}
        onRequestCreateTutor={onRequestCreateTutor}
      />

      <SlidePanel isOpen={isStudentPanelOpen} onClose={() => setIsStudentPanelOpen(false)}>
        <CreateStudentView
          onClose={() => setIsStudentPanelOpen(false)}
          onSuccess={handleStudentCreatedSuccess}
        />
      </SlidePanel>
    </>
  );
};
